import { GitBranch, Plus } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { Link } from "react-router";

import { QueryErrorState } from "@/api/QueryErrorState";
import { queryKeys } from "@/api/hooks";
import { isTerminalQueryError } from "@/api/queryClient";
import {
  usePathwayFamiliesQuery,
  type PathwayFamily,
  type PathwayFamilyDefault,
  type PathwayFamilyVariant,
} from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import { formatDate } from "@/lib/format";
import { libraryAssetHref } from "@/lib/librarySections";
import {
  orderedSteps,
  pathwayFamilyState,
  stepCounts,
} from "@/lib/pathwayFamilies";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card } from "@/ui/Card";
import { Tooltip } from "@/ui/Tooltip";
import { AssetStateBadges } from "@/ui/library/AssetStateBadges";
import {
  EmptyState,
  LoadingState,
  StaleDataNotice,
} from "@/ui/states";

import { LibrarySectionHeader } from "../LibrarySectionHeader";
import { CatalogDetails, CatalogMeta } from "../CatalogRow";
import { NamedReadFailureState } from "./NamedReadFailure";
import { NewPathwayDialog, type NewPathwayType } from "./NewPathwayDialog";
import { PathwayStepSummary } from "./PathwayStepList";

/**
 * The canonical Pathways catalog: explicit families, each with its default and
 * its variants (#643).
 *
 * ## Why this replaces the generic catalog for this one slug
 *
 * `LibraryCatalog` lists `pathway` and `pathway_variant` as a FLAT table of
 * typed assets, which the parity audit recorded as PW-LIB-01: the reference
 * product shows "family accordion containing child variants", Azure shows "flat
 * typed assets with explicit parent identity". The ratified contract closed that
 * gate in favour of the explicit model — a Pathway "is a parent family/defaults
 * asset with versioned variants" — and #642 published the grouping as a route.
 * So the shape a clinician reads is the family, and the identity underneath it
 * is still the explicit parent key: this surface groups NOTHING itself.
 *
 * IT ALSO SHOWS STEPS, which the flat catalog structurally cannot. Catalog reads
 * carry no content by contract; `GET /library/pathway-families` carries each
 * member's steps at its exact head, identity and order only, so a clinician can
 * see what a pathway is made of before opening it — and an engine-owned document
 * is marked as one rather than rendered as authorable.
 *
 * ## Archived families, and the one shape that needs a sentence
 *
 * `Show archived` is the house toggle: ONE state variable drives the query, the
 * paused-retry key, `aria-pressed` AND the visible variant, so what the control
 * claims and what the list shows cannot disagree. That is the audit's PW-LIB-09
 * — a toggle whose state was "unknowable from its label" — answered the way the
 * APG button pattern requires, with a label that does not change under
 * `aria-pressed`.
 *
 * The shape that needs explaining is the service's: with archived rows hidden,
 * an archived DEFAULT is still returned when it owns an active variant, because
 * "omitting it would turn that variant into an apparently parentless row". Such
 * a family says so on its own row rather than being silently mixed in with the
 * active ones — its variants can still take a new version and its default
 * cannot, and only one badge can carry that.
 */

/** Families shown per page, and so the ceiling on rows rendered per click. */
const PAGE_SIZE = 10;

export function PathwayCatalog({
  sectionLabel,
  sectionDescription,
  pageSize = PAGE_SIZE,
}: {
  sectionLabel: string;
  /** The section's one sentence, declared beside its rail entry (#908). */
  sectionDescription: string;
  /**
   * The page window. A prop only so a test can drive the paging with a handful
   * of families; the product renders the default.
   */
  pageSize?: number;
}) {
  const [showArchived, setShowArchived] = useState(false);
  const [creating, setCreating] = useState<{
    assetType: NewPathwayType;
    parentPathwayId?: string;
  } | null>(null);
  const [copying, setCopying] = useState<{
    assetType: NewPathwayType;
    asset: PathwayFamilyDefault | PathwayFamilyVariant;
  } | null>(null);
  // Pages revealed so far. Reset whenever the row set changes shape (the
  // archived toggle), so one click never carries an expanded window into a
  // larger population.
  const [pages, setPages] = useState(1);
  // The family whose heading link should take focus once the next page has
  // rendered — the first newly revealed one. Without it a keyboard user stays
  // on a control that now sits BELOW the rows it just added, and loses focus
  // entirely when the last page removes that control.
  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const familyLinks = useRef(new Map<string, HTMLAnchorElement>());

  const families = usePathwayFamiliesQuery(showArchived);
  const retryFamilies = useRetryPausedQuery(
    queryKeys.libraryPathwayFamilies(showArchived),
  );
  const write = useCapability("library.write");

  const items = families.data?.items ?? [];
  const visible = items.slice(0, pages * pageSize);
  const hidden = items.length - visible.length;
  // Paging is in force at all once the population exceeds one page; the footer
  // then stays MOUNTED through the last reveal so its status line can announce
  // the final state and nothing focused is ever unmounted.
  const paged = items.length > pageSize;

  useEffect(() => {
    if (focusIndex === null) return;
    const target = visible[focusIndex];
    const link = target
      ? familyLinks.current.get(target.default.assetId)
      : undefined;
    if (link) {
      link.focus();
      setFocusIndex(null);
    }
  }, [focusIndex, visible]);

  const createControl = (
    <Tooltip content={write.reason ?? `Create a new pathway in ${sectionLabel}`}>
      <Button
        variant="outline"
        size="sm"
        disabled={write.reason !== null}
        onClick={() => setCreating({ assetType: "pathway" })}
      >
        <Plus aria-hidden="true" className="h-4 w-4" />
        New pathway
      </Button>
    </Tooltip>
  );

  return (
    <>
      {/* #908: the shared library section header. The page-wide "Clinical
          library" banner above the rail is gone, so this section's name is the
          page's `h1`, the breadcrumb says where it sits, and the creation and
          archive controls sit at the top right of the heading row — where the
          reference puts `New pathway parent` — rather than in a toolbar of
          their own below it. */}
      <LibrarySectionHeader
        sectionLabel={sectionLabel}
        description={sectionDescription}
        actions={
          <>
            {createControl}
            {/* The house toggle form: the label stays constant — that is what
                makes `aria-pressed` read as a toggle rather than as a second
                button, and the APG is explicit that a toggle's name must not
                change with its state — and the variant carries the visible
                half. One `showArchived` drives the query, the retry key, the
                pressed state and the variant, so none can disagree. */}
            <Button
              variant={showArchived ? "primary" : "outline"}
              size="sm"
              aria-pressed={showArchived}
              onClick={() => {
                setShowArchived((value) => !value);
                setPages(1);
              }}
            >
              Show archived
            </Button>
          </>
        }
      />
      <section
        aria-label={`${sectionLabel} library assets`}
        className="space-y-4"
      >
        {/* A HELD REFRESH OVER CACHED ROWS IS STILL A FAILED REFRESH.
            `isError` is the SETTLED half only, and it is the half that never
            happens once rows are in hand and the retry is parked: query-core
            keeps `status: "success"` with the previous rows in `data`,
            `fetchStatus: "paused"` and `isError: false`. Without this the list
            below would go on presenting families of unknown age as current. */}
        {families.data ? (
          <StaleDataNotice
            updatedAt={families.dataUpdatedAt}
            onRetry={retryFamilies}
            visible={
              families.isPaused ||
              (families.isError && !isTerminalQueryError(families.error))
            }
          />
        ) : null}

        {families.isPaused && families.data === undefined ? (
          // The held read, which `isPending` cannot express: it stays
          // `"pending"` while nothing is in flight, so the branch below would
          // put a skeleton here permanently.
          <NamedReadFailureState
            failure={families.failureReason}
            isPaused
            retry={retryFamilies}
            subject="Pathway families"
            title="Pathway families could not be loaded"
            description="The Editor Service did not return the shared library's pathway families."
            deniedDescription="You are not authorized to read the shared clinical library."
            offlineDescription="The shared library's pathway families could not be reached. They load on their own when the connection returns."
          />
        ) : families.isPending ? (
          <LoadingState label={`Loading ${sectionLabel} assets`} rows={4} />
        ) : families.isError &&
          (!families.data || isTerminalQueryError(families.error)) ? (
          <QueryErrorState
            error={families.error}
            subject="Pathway families"
            title="Pathway families could not be loaded"
            description="The Editor Service did not return the shared library's pathway families."
            deniedDescription="You are not authorized to read the shared clinical library."
            onRetry={() => void families.refetch()}
          />
        ) : items.length === 0 ? (
          <EmptyState
            title={`No pathways in ${sectionLabel} yet`}
            description={
              showArchived
                ? "No pathway family exists in the library."
                : "No active pathway family is in this section. Archived families stay hidden until shown."
            }
            action={createControl}
          />
        ) : (
          <div className="space-y-4">
            {visible.map((family) => (
              <FamilyCard
                key={family.default.assetId}
                family={family}
                writeRefusal={write.reason}
                onCreateVariant={() =>
                  setCreating({
                    assetType: "pathway_variant",
                    parentPathwayId: family.default.assetId,
                  })
                }
                onCopy={(asset, assetType) => setCopying({ asset, assetType })}
                linkRef={(element) => {
                  const key = family.default.assetId;
                  if (element) familyLinks.current.set(key, element);
                  else familyLinks.current.delete(key);
                }}
              />
            ))}

            {/* The page boundary is stated, never silent: a list that stops at
                ten families without saying so would read as the whole catalog.
                Mounted for as long as paging applies, so the live region is a
                stable node that can announce the final state too. */}
            {paged ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-5 py-3 text-sm text-muted-foreground">
                <span aria-live="polite">
                  {hidden > 0 ? `Showing ${visible.length} of ` : "All "}
                  {items.length}{" "}
                  {showArchived
                    ? "pathway families, archived included"
                    : "active pathway families"}
                  {hidden > 0 ? "" : " shown"}
                </span>
                {hidden > 0 ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFocusIndex(visible.length);
                      setPages((value) => value + 1);
                    }}
                  >
                    Show {Math.min(hidden, pageSize)} more
                  </Button>
                ) : null}
              </div>
            ) : null}
          </div>
        )}
      </section>

      {creating ? (
        <NewPathwayDialog
          assetType={creating.assetType}
          {...(creating.parentPathwayId
            ? { parentPathwayId: creating.parentPathwayId }
            : {})}
          onClose={() => setCreating(null)}
        />
      ) : null}
      {copying ? (
        // The family read carries no `createdBy`/`updatedAt`, and the dialog
        // needs neither: `PathwayCopySource` is exactly the identity a copy
        // reads, so this passes what the response actually said rather than
        // inventing fields to satisfy a wider type.
        <NewPathwayDialog
          assetType={copying.assetType}
          copyFrom={
            copying.asset.assetType === "pathway_variant"
              ? {
                  id: copying.asset.assetId,
                  assetType: "pathway_variant",
                  name: copying.asset.name,
                  latestVersion: copying.asset.latestVersion,
                  parentPathwayId: copying.asset.parentPathwayId,
                }
              : {
                  id: copying.asset.assetId,
                  assetType: "pathway",
                  name: copying.asset.name,
                  latestVersion: copying.asset.latestVersion,
                }
          }
          onClose={() => setCopying(null)}
        />
      ) : null}
    </>
  );
}

/**
 * One explicit family: its default, then its variants.
 *
 * A `section` with its own heading rather than a table row, because a family is
 * a GROUP — the default and its variants are separate assets with separate
 * versions and separate pins, and a table would have to either flatten that
 * (which is the state PW-LIB-01 recorded) or nest one table in another.
 */
function FamilyCard({
  family,
  writeRefusal,
  onCreateVariant,
  onCopy,
  linkRef,
}: {
  family: PathwayFamily;
  writeRefusal: string | null;
  onCreateVariant: () => void;
  onCopy: (
    asset: PathwayFamilyDefault | PathwayFamilyVariant,
    assetType: NewPathwayType,
  ) => void;
  linkRef: (element: HTMLAnchorElement | null) => void;
}) {
  const state = pathwayFamilyState(family);
  const headingId = useId();
  // An archived default is returned in the active family read only so its
  // still-active children do not become apparent orphans. That exception makes
  // the family readable; it does not make the parent writable. The create
  // route refuses an archived parent unconditionally, so opening a dialog here
  // would offer a request already known to fail.
  const createVariantRefusal =
    state === "parent-archived"
      ? "This family default is archived, so it cannot receive a new variant."
      : writeRefusal;

  return (
    <Card className="@container overflow-hidden">
      <section aria-labelledby={headingId} className="min-w-0">
        <div className="flex flex-wrap items-start gap-x-4 gap-y-2 border-b border-border px-5 py-4">
          {/* `grow basis-64`, NOT `flex-1`. `flex-1` sets a zero basis, so the
              name block shrinks to whatever the action buttons leave it rather
              than forcing them onto their own line — and at 414px and below it
              left the family name clipped by its own `truncate`, which
              `tests/visual/pathway-catalog.spec.ts` caught and jsdom cannot.
              A real 16rem basis makes the wrap happen instead of the squeeze. */}
          <div className="min-w-0 grow basis-64">
            <h3
              id={headingId}
              className="flex items-center gap-2 text-sm font-semibold text-foreground"
            >
              <GitBranch
                aria-hidden="true"
                className="h-4 w-4 shrink-0 text-muted-foreground"
              />
              <Link
                ref={linkRef}
                className="min-w-0 truncate"
                to={libraryAssetHref("pathway", family.default.assetId)}
              >
                {family.default.name}
              </Link>
            </h3>
            {/* FAMILY-LEVEL FACTS ONLY. Every per-asset badge — state, version,
                adoption count — belongs to a MEMBER and is rendered on that
                member's own row below. Repeating them here put two identical
                "Family default" badges and two copies of the head version on
                one card, which reads as two different assets to anyone
                scanning it and as a duplicate to a screen reader. */}
            {state === "parent-archived" ? (
              <div className="mt-1 flex flex-wrap items-center gap-2 pl-6">
                {/* THE SERVICE'S OWN SHAPE, said out loud. This family is
                    returned in an ACTIVE listing because it still owns an
                    active variant; calling it plainly archived would tell a
                    clinician their live variant is archived, and saying nothing
                    would hide that the default can take no new version. */}
                <Badge tone="warning">
                  Archived default with active variants
                </Badge>
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {family.default.capabilities.seeded ? (
              <Tooltip content="Part of the base clinical library. The original stays as it is — make an editable copy to create your own version.">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onCopy(family.default, "pathway")}
                >
                  Create editable copy
                </Button>
              </Tooltip>
            ) : null}
            <Tooltip
              content={
                createVariantRefusal ??
                `Create a new variant of ${family.default.name}`
              }
            >
              <Button
                variant="outline"
                size="sm"
                disabled={createVariantRefusal !== null}
                onClick={onCreateVariant}
              >
                <Plus aria-hidden="true" className="h-4 w-4" />
                New variant
              </Button>
            </Tooltip>
          </div>
        </div>

        <MemberRow
          member={family.default}
          memberLabel="Family default"
          href={libraryAssetHref("pathway", family.default.assetId)}
        />

        {family.variants.length === 0 ? (
          <p className="border-t border-border px-5 py-3 text-sm text-muted-foreground">
            This family has no variants
            {family.default.archivedAt === null
              ? "."
              : " that this listing shows."}
          </p>
        ) : (
          <ul aria-label={`Variants of ${family.default.name}`}>
            {family.variants.map((variant) => (
              <li key={variant.assetId} className="border-t border-border">
                <MemberRow
                  member={variant}
                  memberLabel="Variant"
                  href={libraryAssetHref("pathway_variant", variant.assetId)}
                  {...(variant.capabilities.seeded
                    ? { onCopy: () => onCopy(variant, "pathway_variant") }
                    : {})}
                />
              </li>
            ))}
          </ul>
        )}
      </section>
    </Card>
  );
}

/**
 * One member of a family — the default or a variant — compact, with its steps
 * behind a Details disclosure.
 *
 * THE STEPS ARE COLLAPSED BY DEFAULT and that is a size decision, not a
 * de-emphasis: a seeded family default carries eight documents and a catalog
 * that expanded every one of them would put a hundred rows on screen before a
 * clinician had chosen anything.
 *
 * ## WHAT MOVED BEHIND `Details` (#908)
 *
 * The disclosure's own label used to be the sentence "3 authored steps · 5
 * clinical engine documents in Anxiety". "Clinical engine documents" is an
 * internal explanation of how the pinned engine stores a pathway, and the
 * bounded scope removes exactly that kind of copy from normal navigation while
 * keeping the evidence available — so the count of ENGINE documents moved
 * inside the disclosure, next to the step list it describes, and the control is
 * the reference's plain `Details`.
 *
 * THE AUTHORED STEP COUNT STAYED VISIBLE, in the row's metadata line. It is a
 * product fact rather than an engine one, the reference shows the same thing on
 * its own rows ("· 8 steps"), and #643's reason for having it on the surface at
 * all — the disclosure hides the step NAMES, not the fact that there are steps
 * — is unchanged by where the number sits.
 */
function MemberRow({
  member,
  memberLabel,
  href,
  onCopy,
}: {
  member: PathwayFamilyDefault | PathwayFamilyVariant;
  /**
   * Which half of the family this row is — "Family default" or "Variant".
   *
   * NOT named `role`, and the rename is the point rather than a style choice.
   * `jsx-a11y` reads a `role` prop on a component as the ARIA role and rejects
   * any value outside the ARIA vocabulary, which is the right default: a prop
   * that shadows an ARIA attribute name is one refactor away from being spread
   * onto a DOM element as one.
   */
  memberLabel: string;
  href: string;
  onCopy?: () => void;
}) {
  const counts = stepCounts(member.steps);
  const adoption = member.capabilities.adoptionCount;

  return (
    <div className="px-5 py-2.5">
      {/*
        ONE LINE AT THE MATCHED DESKTOP WIDTH, and the arithmetic is why the
        basis is what it is. Beside the application sidebar and the library rail
        this card gives a member row 670px. A SEEDED variant — the ordinary case,
        since every base variant is seeded — spends 266px on its metadata, 163px
        on `Create editable copy`, 55px on `Details` and 36px on three gaps: 520
        fixed, 150 left for the name. At `basis-56` the name asked for 224px, the
        line's hypothetical widths came to 756px, and flex-wrap put both actions
        on a second line — a 78px row where the family default beside it was 42px.
        Wrapping is decided on BASIS, not on the width an item settles at, so a
        `grow` name with a large basis forces the wrap it then absorbs the slack
        from.

        `basis-32` asks for 128px and still grows to ~350px whenever the row
        fits, so no real name is squeezed; `gap-x-3` buys back the last 12px. An
        archived variant adds its `Archived <date>` fact and wraps again, which
        is the right place to spend the second line.
      */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="min-w-0 grow basis-32 text-sm">
          <Link className="font-medium text-foreground" to={href}>
            {member.name}
          </Link>
          <span className="ml-2 font-mono text-xs text-muted-foreground">
            {member.version}
          </span>
        </span>

        {/* ONE flowing line, in the reference's own order and separators
            (`v6.0 · base · updated by … · used by 2 projects`). Each fact stays
            its own element, so the badges keep their shapes and a screen reader
            still hears four things rather than one sentence. */}
        <CatalogMeta
          className="text-xs"
          items={[
            <Badge key="member" tone="outline">
              {memberLabel}
            </Badge>,
            <AssetStateBadges
              key="state"
              capabilities={member.capabilities}
            />,
            <span key="steps">
              {counts.editable} {counts.editable === 1 ? "step" : "steps"}
            </span>,
            <span key="adoption">
              Used by {adoption} {adoption === 1 ? "project" : "projects"}
            </span>,
            member.archivedAt === null ? null : (
              <span key="archived">
                Archived {formatDate(member.archivedAt)}
              </span>
            ),
          ]}
        />

        {onCopy ? (
          // `text-xs`, the scale the rest of this row already uses. At `text-sm`
          // this control is 163px wide and the row's contents overflow its
          // 670px by 43px at the 1280px baseline, which is what put both
          // actions on a second line; the reference draws the same control at
          // the same size as the metadata beside it.
          <Tooltip content="Part of the base clinical library. The original stays as it is — make an editable copy to create your own version.">
            <Button
              variant="ghost"
              size="sm"
              className="text-xs"
              onClick={onCopy}
            >
              Create editable copy
            </Button>
          </Tooltip>
        ) : null}

        {/* At the END of the row, where the reference puts its family chevron,
            with the panel wrapping onto its own line only once opened. The
            subject is in the accessible name, so a page of families does not
            present a dozen identically named disclosures — the audit's
            PW-LIB-09 "repeated ambiguous buttons", answered per control. */}
        <CatalogDetails subject={member.name}>
          {counts.protected > 0 ? (
            <p className="mb-2 text-xs text-muted-foreground">
              {counts.protected} clinical engine{" "}
              {counts.protected === 1 ? "document" : "documents"} in{" "}
              {member.name}, owned by the pinned engine and not authored here.
            </p>
          ) : null}
          {/* Ordered by the SERVER's `order`, never by arrival: the contract
              states the field's meaning, not the array's arrangement. */}
          <PathwayStepSummary
            steps={orderedSteps(member.steps)}
            label={member.name}
          />
        </CatalogDetails>
      </div>
    </div>
  );
}
